/**
 * Ordered tiebreaker list. The host adds criteria, moves them up or down,
 * and removes them. An empty list means tied teams share a rank.
 */
import { Button, Flex, IconButton, Select, Text } from '@radix-ui/themes';
import { TIEBREAKER_OPTIONS, type Tiebreaker } from '../../utils/seasons';

type Criterion = Exclude<Tiebreaker, 'NONE'>;

interface Props {
  value: Criterion[];
  onChange: (next: Criterion[]) => void;
}

const labelOf = (v: Criterion) => TIEBREAKER_OPTIONS.find((o) => o.value === v)?.label ?? v;

const TiebreakerEditor: React.FC<Props> = ({ value, onChange }) => {
  const remaining = TIEBREAKER_OPTIONS.filter((o) => !value.includes(o.value));

  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= value.length) return;
    const next = [...value];
    [next[i], next[j]] = [next[j] as Criterion, next[i] as Criterion];
    onChange(next);
  };

  return (
    <Flex direction="column" gap="2">
      {value.length === 0 ? (
        <Text size="2" color="gray">No tiebreakers. Teams with equal points share a rank.</Text>
      ) : (
        value.map((v, i) => (
          <Flex key={v} align="center" gap="2">
            <Text size="2" style={{ width: 20 }}>{i + 1}.</Text>
            <Text size="2" style={{ flex: 1 }}>{labelOf(v)}</Text>
            <IconButton size="1" variant="soft" color="gray" onClick={() => move(i, -1)} disabled={i === 0} aria-label={`Move ${labelOf(v)} up`}>↑</IconButton>
            <IconButton size="1" variant="soft" color="gray" onClick={() => move(i, 1)} disabled={i === value.length - 1} aria-label={`Move ${labelOf(v)} down`}>↓</IconButton>
            <IconButton size="1" variant="soft" color="red" onClick={() => onChange(value.filter((x) => x !== v))} aria-label={`Remove ${labelOf(v)}`}>×</IconButton>
          </Flex>
        ))
      )}
      <Flex gap="2" align="center" wrap="wrap">
        {remaining.length > 0 && (
          <Select.Root value="" onValueChange={(v) => onChange([...value, v as Criterion])}>
            <Select.Trigger placeholder="Add a tiebreaker…" variant="soft" />
            <Select.Content>
              {remaining.map((o) => (
                <Select.Item key={o.value} value={o.value}>{o.label}</Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        )}
        {value.length > 0 && (
          <Button size="1" variant="ghost" color="gray" onClick={() => onChange([])}>
            None (shared rank)
          </Button>
        )}
      </Flex>
      {value.length > 0 && (
        <Text size="1" color="gray">Teams still tied after the list share a rank.</Text>
      )}
    </Flex>
  );
};

export default TiebreakerEditor;
